// The epic model picker's values (#6753 over #7012's `PUT /v1/plans/:planId/model`): the
// card's epic row shows the epic's STORED `llm` (`plans.llm`, on each job's epic), never the
// prototype's sample `MODELS` list. Its options are `modelPickerOptions` for `general`, the
// lane an untagged slice routes on (`UNTAGGED_DEFAULT_JOB_CLASS`), over the account's keys
// (`useKeys`, never the card's `UtilKey`). A slice tagged onto another lane is the server's to
// check: the door refuses `key_not_usable` only when NO step among the plan's slices is
// served, and the card renders that 422.
//
// A stored pin no option can select (one naming only a `key_label`, which MCP can set, or a
// provider alone) is listed as ONE selected entry, named by its key label, and the first
// change replaces it. Automatic (`llm: null`) reads "Automatic" (#6753 deviation 5).

import {
  modelDisplayName,
  type TaskLlmPreference,
  UNTAGGED_DEFAULT_JOB_CLASS,
} from '@developerz/domain';
import { t } from '../i18n';
import {
  ANSWER_MODEL_AUTOMATIC,
  type AnswerModelOption,
  answerPinOf,
  modelSelectOptions,
  pinValueOf,
} from './answer-model-options';
import type { KeyRow } from './keys-types';

/** The `Select` value of a stored pin no offer names: picking it again changes nothing. */
export const STORED_PIN = '\u0001stored';

/** The picker's value for a stored `llm`: Automatic, a `(provider, model)` pair, or the stored entry. */
export function epicModelValue(llm: TaskLlmPreference | null): string {
  if (llm === null) return ANSWER_MODEL_AUTOMATIC;
  if (llm.provider !== undefined && llm.model !== undefined && llm.key_label === undefined) {
    return pinValueOf(llm.provider, llm.model);
  }
  return STORED_PIN;
}

/** How a stored `llm` reads on the chip and in the live region. */
export function epicModelName(llm: TaskLlmPreference | null): string {
  if (llm === null) return t('fleet.machines.ctl_model_automatic');
  if (llm.key_label !== undefined) {
    return t('fleet.machines.ctl_model_key_label', { label: llm.key_label });
  }
  if (llm.model !== undefined) return modelDisplayName(llm.model);
  return llm.provider ?? t('fleet.machines.ctl_model_automatic');
}

/** The options: Automatic, then the stored entry when no offer can name it, then the offers. */
export function epicModelOptions(
  keys: readonly KeyRow[],
  llm: TaskLlmPreference | null,
): AnswerModelOption[] {
  const value = epicModelValue(llm);
  const currentPin =
    llm?.provider !== undefined && llm.model !== undefined
      ? { provider: llm.provider, model: llm.model }
      : null;
  const [automatic, ...offers] = modelSelectOptions(
    keys,
    { purpose: UNTAGGED_DEFAULT_JOB_CLASS, currentPin: value === STORED_PIN ? null : currentPin },
    {
      automatic: t('fleet.machines.ctl_model_automatic'),
      unavailable: t('fleet.machines.ctl_model_unavailable_hint'),
    },
  );
  const head = automatic === undefined ? [] : [automatic];
  if (value !== STORED_PIN) {
    // A pin the keys no longer list at all still shows as itself rather than a blank.
    const known = offers.some((o) => o.value === value) || value === ANSWER_MODEL_AUTOMATIC;
    return known
      ? [...head, ...offers]
      : [...head, { value, label: epicModelName(llm) }, ...offers];
  }
  return [...head, { value: STORED_PIN, label: epicModelName(llm) }, ...offers];
}

/** A picked value back to the pin the door takes: `null` for Automatic, `undefined` when unreadable. */
export function epicPinOf(value: string): TaskLlmPreference | null | undefined {
  if (value === ANSWER_MODEL_AUTOMATIC) return null;
  if (value === STORED_PIN) return undefined;
  return answerPinOf(value) ?? undefined;
}
